// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fiche d'une demande d'aide (GET /api/dispositifs/aides/demandes/:id) :
//   type (masqué « confidentiel » sans la clé sensible), pièces (une pièce sensible n'est ouverte qu'avec la clé
//   ds_aide_motif_lire, chaque ouverture est tracée), vérification des pièces, décision, paiement préparé à la
//   trésorerie (objet AIDE) puis versement constaté. Jamais de créance. Le motif n'est lu que sur demande (tracé).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Banknote, CheckCircle2, ClipboardCheck, Download, Eye, Gavel, Lock, MoreVertical, Paperclip, Undo2, Upload } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, MenuActions, Squelette } from '../../design/composants';
import { toArray, libelle, aujourdhuiDakar, TYPES_PIECE } from './api';
import { useEspaces, droitsDans } from './espaces';
import { Champ, Etat, AccesRefuseDs, ErreurServeur, ouvrirBlob } from './commun';
import { useSocial, francs, dateLongue, dateHeureLongue, lireMontant, ETATS_AIDE, MODES_PAIEMENT, TYPES_PIECE_AIDE } from './apiSocial';
import { ChoixCompteMode, choixInitial, lireChoix, paiementComplet, champsPaiement } from './ChoixPaiement';
import {
  useLectureSociale, PageFiche, Infos, TitreSection, HistoriqueActes, FenetreActe, MarqueDemo, Montant, motifValide, ChampMotif, ChoixRadio, erreurSociale,
} from './communSocial';
import { LigneDeport } from './Deports';
import { SuiviDossierGestion } from '../../components/suivi/SuiviDemande'; // prochain acteur (lecture)

const AVANT_DECISION = ['DEPOSEE', 'PIECES_A_VERIFIER', 'EN_INSTRUCTION', 'EN_ATTENTE_BUDGET'];
const CIRCUITS = { PRESIDENT: 'Décision du Président', COMMISSION: 'Décision de la commission sociale' };

function FenetrePiece({ aide, onFermer, onFait }) {
  const api = useSocial();
  const [fichier, setFichier] = useState(null);
  const [type, setType] = useState('JUSTIFICATIF');
  const [lib, setLib] = useState('');
  const ok = fichier && fichier.size <= 10 * 1024 * 1024 && lib.trim().length >= 2;
  return (
    <FenetreActe titre="Ajouter une pièce" Icone={Upload} libelleBouton="Déposer la pièce" pret={!!ok} onFermer={onFermer}
      aide={aide.type?.confidentiel ? 'Type confidentiel : la pièce sera marquée sensible.' : 'Une pièce déposée ne se supprime jamais.'}
      onConfirmer={async () => { await api.ajouterPieceAide(aide.id, { fichier, typePiece: type, libelle: lib.trim() }); onFait('Pièce déposée.'); }}>
      <Champ libelle="Fichier" requis aide="PDF, JPEG ou PNG, 10 Mo au plus.">
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="rh-champ-saisie" onChange={(e) => setFichier(e.target.files?.[0] || null)} />
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 12 }}>
        <Champ libelle="Type" requis>
          <select className="rh-champ-saisie" value={type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(TYPES_PIECE_AIDE).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Libellé" requis><input className="rh-champ-saisie" maxLength={200} value={lib} onChange={(e) => setLib(e.target.value)} /></Champ>
      </div>
    </FenetreActe>
  );
}

const REGLES_MONTANT = { STRICT: 'Forfait exact ; barème et plafond bloquants', PLAFOND: 'Jusqu\'au forfait, au barème ou au plafond', AVERTISSEMENT: 'Au-delà : avertissement tracé, sans blocage' };

function FenetreVerifier({ aide, onFermer, onFait }) {
  const api = useSocial();
  const [conformes, setConformes] = useState('OUI');
  const [motif, setMotif] = useState('');
  const [propose, setPropose] = useState('');
  const non = conformes === 'NON';
  const mp = lireMontant(propose);
  return (
    <FenetreActe titre="Vérifier les pièces" Icone={ClipboardCheck} libelleBouton="Enregistrer la vérification" pret={(!non || motifValide(motif, 3)) && (non || !propose.trim() || !!mp)} onFermer={onFermer}
      aide="La personne qui a saisi la demande ne vérifie pas ses pièces. Pièces conformes : la demande passe en instruction avec un montant proposé (instruction) ; sinon un complément est demandé."
      onConfirmer={async () => { await api.verifierAide(aide.id, { conformes: !non, ...(motif.trim() ? { motif: motif.trim() } : {}), ...(!non && mp ? { montant_propose: mp } : {}) }); onFait(non ? 'Complément demandé.' : 'Pièces conformes : demande en instruction.'); }}>
      <ChoixRadio nom="conformes" options={{ OUI: 'Pièces conformes', NON: 'Pièces incomplètes ou non conformes' }} valeur={conformes} onChange={setConformes} />
      {!non && (
        <Champ libelle="Montant proposé (F)" aide={`Facultatif : à défaut, le forfait ou le montant demandé dans la limite du plafond. Règle du type : ${REGLES_MONTANT[aide.regles?.regle_montant] || REGLES_MONTANT.STRICT}.`}>
          <input className="rh-champ-saisie" inputMode="numeric" value={propose} onChange={(e) => setPropose(e.target.value)} />
        </Champ>
      )}
      <ChampMotif valeur={motif} onChange={setMotif} requis={non} libelleChamp={non ? 'Complément attendu' : 'Observation'} aide="3 caractères au moins si un complément est demandé." />
    </FenetreActe>
  );
}

function FenetreDecider({ aide, onFermer, onFait }) {
  const api = useSocial();
  const propose = aide.decision?.montant_propose ?? aide.montant_demande ?? '';
  const [decision, setDecision] = useState('ACCORDER');
  const [montant, setMontant] = useState(propose === '' ? '' : String(propose));
  const [motif, setMotif] = useState('');
  const accorder = decision === 'ACCORDER';
  const m = lireMontant(montant);
  return (
    <FenetreActe titre="Décider de l'aide" Icone={Gavel} libelleBouton={accorder ? 'Accorder' : 'Refuser'} danger={!accorder}
      pret={motifValide(motif, 3) && (!accorder || m)} onFermer={onFermer}
      aide={`${CIRCUITS[aide.regles?.circuit] || 'Décision par la fonction prévue au règlement'}. Ni la personne qui a saisi, ni celle qui a vérifié, ni le bénéficiaire ne décident. Budget épuisé : la demande passe « en attente de budget ».`}
      onConfirmer={async (cle) => { await api.deciderAide(aide.id, accorder ? { decision, montant_accorde: m, motif: motif.trim() } : { decision, motif: motif.trim() }, cle); onFait(accorder ? 'Décision enregistrée.' : 'Aide refusée.'); }}>
      <ChoixRadio nom="decision" options={{ ACCORDER: 'Accorder', REFUSER: 'Refuser' }} valeur={decision} onChange={setDecision} />
      {accorder && (
        <Champ libelle="Montant accordé (F)" requis aide={`${aide.decision?.plafond_type ? `Plafond du type : ${francs(aide.decision.plafond_type)}. ` : 'Aucun plafond du type. '}Règle : ${REGLES_MONTANT[aide.regles?.regle_montant] || REGLES_MONTANT.STRICT}.`}>
          <input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} />
        </Champ>
      )}
      <ChampMotif valeur={motif} onChange={setMotif} aide="3 caractères au moins." />
    </FenetreActe>
  );
}

/** Préparer le paiement de l'aide à la trésorerie (ordre attendu lu au serveur). */
function FenetrePaiementAide({ aide, onFermer, onFait }) {
  const api = useSocial();
  const ordre = useLectureSociale(() => api.ordrePaiementAide(aide.id), [api, aide.id]);
  const treso = useLectureSociale(() => api.tresorerie(aide.organisation_id), [api, aide.organisation_id]);
  const comptes = toArray(treso.donnees?.comptes).filter((c) => c.etat === 'ACTIF');
  const [choix, setChoix] = useState(choixInitial);
  const [date, setDate] = useState(aujourdhuiDakar());
  const [reference, setReference] = useState('');
  const o = ordre.donnees;
  const ch = lireChoix(comptes, choix);
  return (
    <FenetreActe titre="Préparer le paiement de l'aide" Icone={Banknote} libelleBouton="Préparer le paiement" pret={!!(o && paiementComplet(ch) && date)} onFermer={onFermer}
      aide="Le paiement sera signé, contresigné puis exécuté par d'autres personnes à la trésorerie ; le versement est constaté ensuite sur cette fiche."
      onConfirmer={async (cle) => {
        await api.preparerPaiement(aide.organisation_id, {
          sens: o.sens, objet_type: o.objet_type, objet_id: o.objet_id, type_operation: o.type_operation, decision_id: o.decision_id,
          beneficiaire_employe_id: o.beneficiaire_employe_id, libelle: o.libelle, montant: o.montant, date_valeur: date,
          ...champsPaiement(ch), ...(reference.trim() ? { reference: reference.trim() } : { reference: o.reference }),
        }, cle);
        onFait('Paiement préparé : il attend les signatures à la trésorerie.');
      }}>
      {ordre.erreur && <ErreurServeur erreur={ordre.erreur} />}
      {treso.erreur && <ErreurServeur erreur={treso.erreur} />}
      {!o && !ordre.erreur && <Squelette largeur="60%" />}
      {o && (
        <>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}><strong>{o.libelle}</strong> — <Montant n={o.montant} fort /></p>
          {o.tresorerie_disponible === false && <BandeauInfo ton="alerte">La trésorerie de démonstration n'est pas installée pour cette organisation.</BandeauInfo>}
          <ChoixCompteMode comptes={comptes} lecture={treso} valeur={choix} onChange={setChoix} />
          <Champ libelle="Date de valeur" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} style={{ maxWidth: 220 }} /></Champ>
          <Champ libelle="Référence" aide={`Par défaut : ${o.reference}.`}>
            <input className="rh-champ-saisie" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} />
          </Champ>
        </>
      )}
    </FenetreActe>
  );
}

/** Constater le versement : choisir le paiement EXÉCUTÉ de cette aide. */
function FenetreVerser({ aide, onFermer, onFait }) {
  const api = useSocial();
  const paiements = useLectureSociale(() => api.paiements(aide.organisation_id, { page: 1, taille: 100, statut: 'EXECUTE', sens: 'DECAISSEMENT' }), [api, aide.organisation_id]);
  const candidats = toArray(paiements.donnees).filter((p) => p.objet_type === 'AIDE' && p.objet_id === aide.id);
  const [choix, setChoix] = useState('');
  const p = candidats.find((x) => x.id === choix) || (candidats.length === 1 ? candidats[0] : null);
  return (
    <FenetreActe titre="Constater le versement" Icone={CheckCircle2} libelleBouton="Constater le versement" pret={!!p} onFermer={onFermer}
      aide="Acte du trésorier, jamais de la personne qui a décidé l'aide ni du bénéficiaire."
      onConfirmer={async (cle) => { await api.verserAide(aide.id, { paiement_id: p.id, ...(p.date_valeur ? { date_valeur: String(p.date_valeur).slice(0, 10) } : {}) }, cle); onFait('Versement constaté : l\'aide est versée.'); }}>
      {paiements.erreur && <ErreurServeur erreur={paiements.erreur} />}
      {!paiements.donnees && !paiements.erreur && <Squelette largeur="60%" />}
      {paiements.donnees && !candidats.length && <p style={{ fontSize: 14 }}>Aucun paiement exécuté pour cette aide : préparez-le, puis faites-le signer et exécuter à la trésorerie.</p>}
      {candidats.length > 1 && (
        <Champ libelle="Paiement exécuté" requis>
          <select className="rh-champ-saisie" value={choix} onChange={(e) => setChoix(e.target.value)}>
            <option value="">Choisir…</option>
            {candidats.map((x) => <option key={x.id} value={x.id}>{x.numero} — {francs(x.montant)}</option>)}
          </select>
        </Champ>
      )}
      {p && <p style={{ fontSize: 14, margin: '0 0 12px' }}>Paiement {p.numero} : {francs(p.montant)}, {libelle(MODES_PAIEMENT, p.mode).toLowerCase()}, valeur au {dateLongue(String(p.date_valeur).slice(0, 10))}.</p>}
    </FenetreActe>
  );
}

function FenetreAnnuler({ aide, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre="Annuler la demande" Icone={Undo2} libelleBouton="Annuler la demande" danger pret={motifValide(motif)} onFermer={onFermer}
      aide="Avant la décision : par le guichet ; après l'accord et avant le paiement : par le décideur."
      onConfirmer={async (cle) => { await api.annulerAide(aide.id, motif.trim(), cle); onFait('Demande annulée.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

function PiecesAide({ aide }) {
  const api = useSocial();
  const [erreurs, setErreurs] = useState({});
  const pieces = toArray(aide.pieces);
  const ouvrir = async (p) => {
    setErreurs((x) => ({ ...x, [p.id]: null }));
    try {
      const f = await api.fichierPieceAide(aide.id, p.id);
      ouvrirBlob(f, `${(p.libelle || 'piece').replace(/[^\w\-À-ÿ ]+/g, '').trim().slice(0, 60) || 'piece'}${f.type === 'application/pdf' ? '.pdf' : f.type === 'image/png' ? '.png' : '.jpg'}`);
    } catch (e) {
      let err = erreurSociale(e);
      const r = e?.response;
      if (r && typeof Blob !== 'undefined' && r.data instanceof Blob) { try { const d = JSON.parse(await r.data.text()); err = { ...err, message: d.message || err.message }; } catch { /* corps non JSON */ } }
      setErreurs((x) => ({ ...x, [p.id]: err.message }));
    }
  };
  return (
    <>
      {toArray(aide.pieces_manquantes).length > 0 && (
        <BandeauInfo ton="alerte" titre="Pièces attendues">{toArray(aide.pieces_manquantes).map((t) => libelle(TYPES_PIECE, t.type_piece || t)).join(', ')}</BandeauInfo>
      )}
      {!pieces.length ? <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucune pièce déposée.</p> : (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Pièce</th><th>Déposée le</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {pieces.map((p) => (
                <tr key={p.id}>
                  <td>
                    <span className="rh-cellule-principale" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{p.sensible ? <Lock size={14} aria-hidden="true" /> : <Paperclip size={14} aria-hidden="true" />}{p.libelle}</span>
                    <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{libelle(TYPES_PIECE, p.type_piece)}{p.sensible ? ' · sensible : ouverture tracée' : ''}</span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{dateLongue(p.deposee_le)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Bouton petit variante="discret" Icone={Download} onClick={() => ouvrir(p)}>Ouvrir</Bouton>
                    {erreurs[p.id] && <div style={{ fontSize: 12, color: 'var(--sg-danger)', marginTop: 4, maxWidth: 260, marginLeft: 'auto' }}>{erreurs[p.id]}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Motif({ aide }) {
  const api = useSocial();
  const [motif, setMotif] = useState(null);
  const [erreur, setErreur] = useState(null);
  if (!aide.motif?.existe) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun motif saisi.</p>;
  if (!aide.motif.lisible) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Motif confidentiel : réservé aux personnes habilitées.</p>;
  if (motif) return <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 14 }}>{motif.motif}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>Saisi le {dateLongue(motif.saisi_le)} · votre lecture a été tracée.</span></p>;
  return (
    <div>
      <Bouton petit Icone={Eye} onClick={async () => { setErreur(null); try { setMotif(await api.motifAide(aide.id)); } catch (e) { setErreur(erreurSociale(e)); } }}>Lire le motif</Bouton>
      <span className="rh-form-aide" style={{ marginLeft: 8 }}>Chaque lecture est tracée.</span>
      <ErreurServeur erreur={erreur} />
    </div>
  );
}

export default function AidePage() {
  const { org, id } = useParams();
  const api = useSocial();
  const { espaces } = useEspaces();
  const fiche = useLectureSociale(() => api.aide(id), [api, id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const a = fiche.donnees;
  const droits = droitsDans(espaces, a?.organisation_id || org);
  const fait = (m) => { setFenetre(null); setInfo(m); fiche.recharger(); };
  const s = a?.statut;
  const menu = a ? [
    ['DEPOSEE', 'PIECES_A_VERIFIER', 'EN_INSTRUCTION'].includes(s) && droits.a('ds_aide_saisir_guichet') && { libelle: 'Ajouter une pièce', Icone: Upload, onClick: () => setFenetre('piece') },
    ((AVANT_DECISION.includes(s) && droits.a('ds_aide_saisir_guichet')) || (s === 'ACCORDEE' && droits.a('ds_aide_decider')))
      && { libelle: 'Annuler la demande', Icone: Undo2, danger: true, onClick: () => setFenetre('annuler') },
  ].filter(Boolean) : [];
  const actions = a ? (
    <>
      {['DEPOSEE', 'PIECES_A_VERIFIER'].includes(s) && droits.a('ds_aide_verifier_pieces') && <Bouton variante="primaire" Icone={ClipboardCheck} onClick={() => setFenetre('verifier')}>Vérifier les pièces</Bouton>}
      {['EN_INSTRUCTION', 'EN_ATTENTE_BUDGET'].includes(s) && droits.a('ds_aide_decider') && <Bouton variante="primaire" Icone={Gavel} onClick={() => setFenetre('decider')}>Décider</Bouton>}
      {s === 'ACCORDEE' && droits.a('ds_paiement_preparer') && <Bouton variante="primaire" Icone={Banknote} onClick={() => setFenetre('paiement')}>Préparer le paiement</Bouton>}
      {s === 'ACCORDEE' && droits.a('ds_paiement_executer') && <Bouton Icone={CheckCircle2} onClick={() => setFenetre('verser')}>Constater le versement</Bouton>}
      {menu.length > 0 && <MenuActions iconeSeule Icone={MoreVertical} libelle="Autres actions" items={menu} />}
    </>
  ) : null;
  return (
    <PageFiche section="aides" libelleSection="Aides" titre={a ? `Aide ${a.numero}` : 'Aide'} actions={actions}>
      {fiche.erreur && (fiche.erreur.statut === 403 || fiche.erreur.statut === 404
        ? <section className="rh-carte"><AccesRefuseDs erreur={fiche.erreur} /></section>
        : <ErreurServeur erreur={fiche.erreur} />)}
      {!a && !fiche.erreur && <Squelette hauteur={160} />}
      {a && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '-8px 0 16px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
            <Etat table={ETATS_AIDE} code={a.statut} />
            <span>{a.salarie?.nom}</span><span aria-hidden="true">·</span><span>{a.salarie?.matricule}</span>
            <span aria-hidden="true">·</span><span>{a.type?.libelle}</span>
            {a.type?.confidentiel && <Badge ton="neutre">Confidentiel</Badge>}
            <MarqueDemo actif={a.donnees_demo} />
          </div>
          <SuiviDossierGestion type="DS_AIDE" id={id} cle={fiche.donnees} />
          {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
          {s === 'EN_ATTENTE_BUDGET' && <BandeauInfo ton="alerte" titre="En attente de budget">Le budget annuel du dispositif est épuisé : la décision reste en attente d'un budget complémentaire.</BandeauInfo>}
          <section className="rh-carte">
            <TitreSection>Demande</TitreSection>
            <Infos lignes={[
              ['Type d\'aide', a.type?.libelle], ['Forme', a.regles?.forme === 'FORFAIT' ? 'Forfait' : a.regles?.forme === 'BAREME' ? 'Barème' : a.regles?.forme === 'CAS_PAR_CAS' ? 'Cas par cas' : null],
              ['Montant demandé', a.montant_demande !== null ? <Montant n={a.montant_demande} /> : null], ['Montant accordé', a.montant_accorde !== null ? <Montant n={a.montant_accorde} fort /> : null],
              ['Date de l\'événement', dateLongue(a.date_evenement)], ['Ayant droit', a.ayant_droit === 'CONJOINT' ? 'Conjoint' : a.ayant_droit === 'ENFANT' ? 'Enfant' : null],
              ['Déposée le', dateHeureLongue(a.depose_le)], ['Versée le', dateLongue(a.verse_le)], ['Circuit', CIRCUITS[a.regles?.circuit]],
              ['Forfait du règlement', a.regles?.montant_forfait ? francs(a.regles.montant_forfait) : null], ['Plafond par cas', a.regles?.plafond_cas ? francs(a.regles.plafond_cas) : null],
            ]} />
          </section>
          <section className="rh-carte">
            <TitreSection>Pièces</TitreSection>
            <PiecesAide aide={a} />
          </section>
          <section className="rh-carte">
            <TitreSection>Instruction et décision</TitreSection>
            {a.decision ? (
              <Infos lignes={[
                ['Décision', a.decision.statut === 'PROPOSEE' ? 'Proposée, en attente' : a.decision.statut === 'ACCORDEE' || a.decision.statut === 'ACCORDEE_MODIFIEE' ? 'Accordée' : a.decision.statut === 'REFUSEE' ? 'Refusée' : a.decision.statut],
                ['Montant demandé', a.montant_demande !== null ? <Montant n={a.montant_demande} /> : null],
                ['Montant proposé (instruction)', a.decision.montant_propose ? <Montant n={a.decision.montant_propose} /> : null], ['Plafond du type', a.decision.plafond_type ? <Montant n={a.decision.plafond_type} /> : 'Aucun'],
                ['Montant accordé (décision)', a.montant_accorde !== null ? <Montant n={a.montant_accorde} fort /> : null],
                ['Règle de montant', REGLES_MONTANT[a.regles?.regle_montant] || null],
                ['Avertissements', a.decision.avertissements?.length ? a.decision.avertissements.join(' ') : null],
                ['Décidée le', dateLongue(a.decision.decide_le)], ['Motif de la décision', a.decision.motif],
              ]} />
            ) : <p className="rh-cellule-secondaire" style={{ margin: 0 }}>La proposition de décision naît quand les pièces sont vérifiées conformes.</p>}
            {a.annulation && <BandeauInfo ton="info" titre={`Annulée le ${dateLongue(a.annulation.le)}`}>{a.annulation.motif}</BandeauInfo>}
            <h3 style={{ fontSize: 14, margin: '16px 0 8px' }}>Motif de la demande</h3>
            <Motif aide={a} />
          </section>
          <section className="rh-carte">
            <TitreSection>Historique</TitreSection>
            <HistoriqueActes lignes={a.historique} etats={ETATS_AIDE} />
          </section>
        </>
      )}
      {fenetre === 'piece' && <FenetrePiece aide={a} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre === 'verifier' && <FenetreVerifier aide={a} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre === 'decider' && <FenetreDecider aide={a} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre === 'paiement' && <FenetrePaiementAide aide={a} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre === 'verser' && <FenetreVerser aide={a} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre === 'annuler' && <FenetreAnnuler aide={a} onFermer={() => setFenetre(null)} onFait={fait} />}
      {a && <LigneDeport organisationId={a.organisation_id || org} salarie={a.salarie?.id ? a.salarie : null} droits={droits} />}
    </PageFiche>
  );
}
